import React, { useState, useEffect } from 'react';
import {
    Box,
    Tooltip,
    Chip,
    Grid,
    Stack,
    Select,
    Typography,
    TextField,
    IconButton,
    MenuItem,
    InputAdornment,
    Button
} from '@mui/material'
import { Add, Download, Delete, Replay, Search, Edit } from '@mui/icons-material' // Thêm Edit icon
import Tables from '@components/table';
import apiService from '@services/common'
import { useGlobalContext } from '@providers/globalProvider';
import AddDrawer from './addDrawer';
import EditDrawer from './editDrawer'; // Import EditDrawer
import { useSelector, useDispatch } from 'react-redux'
import { updateBreadcrumb } from '@redux/commonReducer'
import ChangePasswordModal from './changePasswordModal';

const PageCategory = () => {
    const [data, setData] = useState([])
    const [role, setRole] = useState([])
    const [keyword, setKeyword] = useState('')
    const [selectedRows, setSelectedRows] = useState([])
    const { showNotification, showLoading, hideLoading, openModal, closeModal, openDrawer, closeDrawer, showConfirm, closeConfirm } = useGlobalContext()
    const dispatch = useDispatch()

    // Cập nhật cột action để thêm nút Edit
    const columns = [
        {
            field: 'avatar',
            headerName: 'Avatar',
            width: 120,
            editable: false,
            renderCell: (params) => {
                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        py: 1
                    }}>
                        <img
                            src={params.value ? process.env.REACT_APP_BACKEND_URL + params.value : '/noimg.jpeg'}
                            alt="avatar"
                            style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                            }}
                        />
                    </Box>
                )
            }
        },
        {
            field: 'name',
            headerName: 'Tên hiển thị',
            width: 200,
            editable: true,
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 300,
            editable: true,
        },
        {
            field: 'roles',
            headerName: 'Thuộc nhóm quyền',
            width: 300,
            editable: true,
            renderCell: (params) => {
                const roles = params.row?.roles || [];

                const roleColors = {
                    admin: 'error',
                    manager: 'primary',
                    staff: 'success',
                    editor: 'warning',
                    viewer: 'info',
                };

                return (
                    <>
                        {roles.map((item, index) => (
                            <Chip
                                key={index}
                                label={item.name}
                                color={roleColors[item.name] || "default"}
                                size="small"
                                sx={{ mr: 0.5 }}
                            />
                        ))}
                    </>
                );
            },
            renderEditCell: (params) => (
                <Select
                    value={params.value !== undefined ? String(params.value) : ""}
                    onChange={(event) => {
                        apiService.post(`assign-role/${params.row.id}`, {
                            role: event.target.value,
                        })
                            .then(res => {
                                loadData();
                                showNotification("Update thành công", 'success');
                                hideLoading();
                            })
                            .catch(err => {
                                loadData();
                                showNotification("Đã có lỗi xảy ra", 'error');
                                hideLoading();
                            });
                    }}
                    fullWidth
                >
                    {role.map((item, index) => (
                        <MenuItem
                            key={index}
                            value={item.name}
                            selected={params.value === item.id}
                        >
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
            )
        },
        {
            field: 'created_at',
            headerName: 'Ngày tạo',
            width: 120,
            editable: false,
            renderCell: (params) => new Date(params.value).toLocaleDateString('vi-VN')
        },
        {
            field: 'action',
            headerName: 'Hành động',
            width: 250, // Tăng width để chứa thêm nút
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    {/* Nút Edit */}
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<Edit />}
                        onClick={() => handleOpenEditDrawer(params.row)}
                    >
                        Sửa
                    </Button>

                    {/* Nút Đổi mật khẩu */}
                    <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={() => handleOpenChangePasswordModal(params.row)}
                    >
                        Đổi mật khẩu
                    </Button>
                </Stack>
            ),
        }
    ];

    const [selectedUser, setSelectedUser] = useState(null);

    // Hàm mở Edit Drawer
    const handleOpenEditDrawer = (user) => {
        openDrawer({
            content: <EditDrawer
                user={user}
                submitFunction={(formData) => doUpdate(formData, user)} // Truyền user vào doUpdate
                onClose={() => closeDrawer()}
            />,
            onCloseCallback: () => {
                // Không cần setSelectedUser nữa
            }
        });
    };

    // Hàm xử lý update
    const doUpdate = (formData, user) => {
        showLoading();
        const userId = user?.id;
        if (!userId) {
            hideLoading();
            showNotification('Không tìm thấy ID người dùng', 'error');
            return;
        }

        console.log('=== Debug doUpdate ===');
        console.log('User ID:', userId);
        console.log('SelectedUser state:', selectedUser); // Có thể là null
        console.log('User param:', user); // Chắc chắn có giá trị

        apiService.putWithMedia(`user/${userId}`, formData)
            .then(res => {
                loadData();
                hideLoading();
                closeDrawer();
                showNotification('Cập nhật người dùng thành công', 'success');
            })
            .catch(err => {
                hideLoading();
                const errorMsg = err.response?.data?.message || err.message || 'Có lỗi xảy ra';
                showNotification(errorMsg, 'error');
            });
    };

    const handleOpenChangePasswordModal = (user) => {
        setSelectedUser(user);
        openModal(
            <ChangePasswordModal
                open={true}
                onClose={handleCloseChangePasswordModal}
                user={user}
                onSuccess={() => {
                    handleCloseChangePasswordModal();
                    showNotification('Đổi mật khẩu thành công', 'success');
                }}
            />
        );
    };

    const handleCloseChangePasswordModal = () => {
        closeModal();
        setSelectedUser(null);
    }

    const breadcrumb =
        [
            {
                name: 'Quản lý người dùng',
                url: 'user'
            }
        ]

    const styleButton = {
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        },
        border: 'none'
    }

    const processRowUpdate = (newRow, oldRow) => {
        showLoading()
        setData((prevRows) =>
            prevRows.map((row) => (row.id === oldRow.id ? { ...newRow } : row))
        )
        apiService.put(`user/${newRow.id}`, newRow)
            .then(res => {
                showNotification("Update thành công", 'success');
                hideLoading()
            })
            .catch(err => {
                showNotification("Đã có lỗi xảy ra, liên hệ quản trị viên để được hỗ trợ", 'error');
                hideLoading()
            })
        return newRow
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            apiService.get(`user?keyword=${keyword}`)
                .then(res => {
                    setData(res.data.data)
                })
                .catch(err => {
                    showNotification("Đã có lỗi xảy ra, liên hệ quản trị viên để được hỗ trợ", 'error');
                })
        }
    }

    const handleAddNew = () => {
        openDrawer({
            content: <AddDrawer submitFunction={doAddNew} />,
            onCloseCallback: () => { }
        })
    }

    const doAddNew = (data) => {
        showLoading()

        apiService.postWithMedia(`user`, data)
            .then(res => {
                loadData()
                hideLoading()
                showNotification(
                    'Thêm mới người dùng thành công',
                    'success'
                );
            })
            .catch(err => {
                hideLoading()
                showNotification(err.message, 'error')
            })
    }

    const doDelete = () => {
        showLoading()
        apiService.delete(`user/${selectedRows}`)
            .then(res => {
                loadData()
                setSelectedRows([])
                closeConfirm()
                showNotification('Xoá thành công', 'success')
            })
            .catch(err => {
                showNotification(err.message, 'error')
            })
    }

    const handleDelete = () => {
        showConfirm({
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc muốn xoá bản ghi này? Sau khi xoá, dữ liệu có thể không khôi phục lại được',
            onConfirm: () => doDelete(),
            oncancel: () => closeConfirm()
        })
    }

    const loadData = () => {
        showLoading()
        apiService.get('user')
            .then(res => {
                setData(res.data.data)
                hideLoading()
            })
            .catch(err => {
                console.log(err)
                showNotification('Đã có lỗi - vui lòng liên hệ với quản trị viên', 'error')
                hideLoading()
            })
    }

    const handleChangeKeyword = (e) => {
        setKeyword(e.target.value)
    }

    useEffect(() => {
        apiService.get('role')
            .then(res => setRole(res.data.data))
            .catch(err => console.error(err))
    }, [])

    useEffect(() => {
        loadData()
        dispatch(updateBreadcrumb(breadcrumb))
    }, [])

    return (
        <Box sx={{
            width: '100%'
        }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant='h5'>Người dùng</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            hiddenLabel
                            defaultValue={keyword}
                            onChange={handleChangeKeyword}
                            onKeyDown={handleKeyDown}
                            placeholder="Nhập từ khóa tìm kiếm..."
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6 }}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                    >
                        <Stack direction={'row'} spacing={0}>
                            <Tooltip placement="bottom" content="Thêm mới">
                                <IconButton
                                    sx={styleButton}
                                    onClick={() => handleAddNew()}
                                >
                                    <Add />
                                </IconButton>
                            </Tooltip>
                            <Tooltip placement="bottom" content="Xoá">
                                <IconButton
                                    sx={styleButton}
                                    disabled={!(selectedRows.length > 0)}
                                    onClick={() => handleDelete()}
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                            <Tooltip placement="bottom" content="Export excel">
                                <IconButton
                                    sx={styleButton}
                                    onClick={() => loadData()}
                                >
                                    <Replay />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box>
                {data &&
                    data.length > 0 &&
                    <Tables
                        rows={data}
                        columns={columns}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        processRowUpdate={processRowUpdate}
                        rowHeight={80}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-cell[data-field="avatar"]': {
                                justifyContent: 'center',
                            }
                        }}
                    />
                }
            </Box>
        </Box>
    );
}

export default PageCategory;